import type { SectionUserMenuItemProps } from './SectionUserMenu';

import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { AvatarIcon, CaretDownIcon, CaretLeftIcon } from '../../images/shapes';

import { SectionUserMenu } from './SectionUserMenu';

export const SectionToggleSidebar = ({
  onClick,
  isOpen,
  testId,
}: {
  onClick?: () => void;
  isOpen?: boolean;
  testId?: string;
}) => {
  return (
    <button
      type="button"
      className={twMerge(
        'absolute -top-3 sm:top-0 -right-8 left-auto sm:right-auto h-full w-8 sm:w-4 mb-safe sm:mb-0',
        'justify-center items-center cursor-pointer flex z-10 text-white',
        'bg-blue sm:bg-white dark:sm:dark:bg-slate-950 rounded-r-xl sm:rounded-r-2xl',
        isOpen && 'top-0',
      )}
      onClick={onClick}
      {...getTestProps(testId)}
    >
      <CaretLeftIcon
        className={twMerge(
          'h-5 sm:h-3 text-white dark:text-white sm:text-slate-900',
          'transition-all',
          'ease-linear',
          'duration-normal',
          'hover:opacity-50',
          'select-none',
          !isOpen && 'rotate-180',
        )}
        {...getTestProps(testId, 'icon')}
      />
    </button>
  );
};

const sectionUserProfilePropAvatar = (
  <AvatarIcon className={twMerge('w-12 pl-3 flex select-none text-white')} />
);

export interface SectionUserProfileProps {
  /**
   * Images for user avatar
   */
  avatar?: any;
  /**
   * User full name
   */
  name?: string;
  /**
   * User email
   */
  email?: string;
  /**
   * Menu item show on click
   */
  menuItem?: SectionUserMenuItemProps[];
  /**
   * onClick
   */
  onClick?: () => void;
  /**
   * Mode if is open/close
   */
  isOpen?: boolean;
  /**
   * Is Modal On Close to defined if we open when is closed
   */
  isModalOnClose?: boolean;
  /**
   * is Modal On Close Outside will be open and show on right
   */
  isModalOnCloseOutside?: boolean;
  /**
   * handle Sidebar Open
   */
  handleSidebarOpen?: () => void;
  /**
   * size Close is helper props to defined width of the closed
   */
  sizeClose?: number;
  /**
   * Height of the user profile
   */
  height?: number;
  /**
   * Additional User Container Classes
   */
  additionalUserContainerClasses?: string;
  /**
   * Additional Username Classes
   */
  additionalUserNameClasses?: string;
  /**
   * Additional User Email Classes
   */
  additionalUserEmailClasses?: string;
  /**
   * Additional User Menu Container Classes
   */
  additionalUserMenuContainerClasses?: string;
  /**
   * Additional User Menu Classes
   */
  additionalUserMenuClasses?: string;
  /**
   * Add event to close modal after click outside
   */
  hasEventOnCloseOutside?: boolean;
  /**
   * Component test id
   */
  testId?: string;
}

export const SectionUserProfile = ({
  avatar = sectionUserProfilePropAvatar,
  name = '',
  email = '',
  menuItem,
  onClick,
  isOpen = true,
  isModalOnClose = false,
  isModalOnCloseOutside = false,
  handleSidebarOpen,
  sizeClose = 80,
  height = 64,
  additionalUserContainerClasses,
  additionalUserNameClasses,
  additionalUserMenuContainerClasses,
  additionalUserMenuClasses,
  hasEventOnCloseOutside = false,
  testId,
}: SectionUserProfileProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const validWhenModal = isModalOnClose ? true : isOpen;

  useEffect(() => {
    if (!isOpen) {
      setIsModalOpen(false);
    }
  }, [isOpen]);

  const handleOpen = () => {
    if (!isOpen && handleSidebarOpen && !isModalOnCloseOutside) {
      handleSidebarOpen();
    }

    setIsModalOpen((prevState) => !prevState);
  };

  return (
    <div
      className={twMerge(
        'relative',
        'flex',
        'items-center',
        'mb-safe',
        'bg-blue',
        'w-full',
        'transition-all',
        'ease-in-out',
        'duration-normal',
        additionalUserContainerClasses,
      )}
      style={{
        height: height || 64,
        width: !isOpen ? sizeClose : '100%',
      }}
      {...getTestProps(testId)}
    >
      <SectionToggleSidebar
        onClick={onClick}
        isOpen={isOpen}
        {...getTestProps(testId, 'toggle', 'testId')}
      />

      {isModalOpen && menuItem && menuItem?.length > 0 && (
        <SectionUserMenu
          items={menuItem}
          avatar={avatar}
          name={name}
          email={email}
          handleClose={() => setIsModalOpen(false)}
          isModalOnCloseOutside={isModalOnCloseOutside}
          leftOnOutside={sizeClose}
          isOpen={isOpen}
          additionalUserMenuContainerClasses={
            additionalUserMenuContainerClasses
          }
          additionalUserMenuClasses={additionalUserMenuClasses}
          hasEventOnCloseOutside={hasEventOnCloseOutside}
          {...getTestProps(testId, 'menu', 'testId')}
        />
      )}

      <div
        title={!isOpen ? name + ' - ' + email : ''}
        className={twMerge(
          'flex items-center w-full h-full pl-0 sm:pl-4',
          validWhenModal &&
            menuItem &&
            menuItem?.length > 0 &&
            'cursor-pointer',
        )}
        {...(validWhenModal && { onClick: handleOpen })}
        {...getTestProps(testId, 'container')}
      >
        {avatar && (
          <AvatarIcon
            className={twMerge('w-12 pl-3 flex select-none text-white')}
          />
        )}

        <div
          className={twMerge(
            'relative flex flex-col ml-3 overflow-hidden transition-all ease-linear duration-fast',
            isOpen ? 'w-full' : 'w-0',
          )}
        >
          <div
            title={name?.length > 13 ? name : undefined}
            className={twMerge(
              'text-base text-white whitespace-nowrap relative overflow-hidden text-ellipsis pr-10',
              additionalUserNameClasses,
            )}
            {...getTestProps(testId, 'name')}
          >
            {name}
          </div>
          <CaretDownIcon
            className={twMerge(
              'absolute top-2 right-4 w-3 text-white cursor-pointer transition-all ease-linear duration-normal',
              isModalOpen && 'rotate-180',
              menuItem?.length === 0 && 'hidden',
            )}
            {...getTestProps(testId, 'arrow')}
          />
        </div>
      </div>
    </div>
  );
};
